import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { HC } from "../hr/theme";
import { Btn, Pill } from "../hr/ui";
import { DataTable, cmoney, num, dt } from "./crmui";
import { toast } from "sonner";
import { Plus, Copy, Eye, Download, Users } from "lucide-react";

const BLANK = { name: "", description: "", conditions: { match: "all", rules: [] }, status: "active" };

export default function Segments() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [members, setMembers] = useState(null);

  const load = useCallback(() => {
    api.get("/crm/segments").then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); api.get("/crm/segments/fields").then(setMeta).catch(() => {}); }, [load]);

  const valueOptions = (field) => {
    const f = (meta?.fields || []).find((x) => x.field === field);
    if (!f) return null;
    if (f.type === "location") return (meta.locations || []).map((l) => ({ value: l.id, label: l.name }));
    if (f.type === "tier") return (meta.tiers || []).map((t) => ({ value: t, label: t }));
    if (f.type === "channel") return (meta.channels || []).map((c) => ({ value: c, label: c }));
    if (f.type === "status") return (meta.statuses || []).map((s) => ({ value: s, label: s }));
    if (f.type === "verification") return (meta.verification || []).map((s) => ({ value: s, label: s }));
    if (f.type === "category") return (meta.categories || []).map((c) => ({ value: c, label: c }));
    if (f.type === "boolean") return [{ value: "true", label: "Yes" }, { value: "false", label: "No" }];
    return null;
  };

  const setRule = (i, patch) => {
    const rules = [...edit.conditions.rules];
    rules[i] = { ...rules[i], ...patch };
    setEdit({ ...edit, conditions: { ...edit.conditions, rules } });
  };

  const runPreview = async () => {
    try {
      const r = await api.post("/crm/segments/preview", { conditions: edit.conditions });
      setPreview(r);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!edit.name) { toast.error("Segment name is required"); return; }
    try {
      if (edit.id) await api.put(`/crm/segments/${edit.id}`, edit);
      else await api.post("/crm/segments", edit);
      toast.success("Segment saved");
      setEdit(null); setPreview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewMembers = async (s) => {
    try { setMembers(await api.get(`/crm/segments/${s.id}/members`)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportMembers = async (s) => {
    try {
      const res = await api.get(`/crm/segments/${s.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `segment-${s.name}.csv`; a.click();
      toast.success("Members exported");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="crm-segments-page">
      <div className="flex items-center gap-2">
        <div className="flex-1" />
        <Btn onClick={() => { setEdit(JSON.parse(JSON.stringify(BLANK))); setPreview(null); }} data-testid="segment-new">
          <Plus size={14} /> New Segment
        </Btn>
      </div>

      <DataTable testid="crm-segments-table" rows={rows}
        columns={[["name", "Segment"], ["description", "Description"], ["customer_count", "Customers"],
                  ["status", "Status"], ["created_by", "Created by"], ["last_evaluated_at", "Last evaluated"], ["actions", "Actions"]]}
        render={{
          status: (r) => <Pill status={r.status === "active" ? "active" : "draft"} text={r.status} />,
          last_evaluated_at: (r) => dt(r.last_evaluated_at),
          customer_count: (r) => num(r.customer_count),
          actions: (r) => (
            <span className="flex gap-1.5">
              <button onClick={(e) => { e.stopPropagation(); viewMembers(r); }} data-testid={`segment-members-${r.id.slice(0, 4)}`}
                className="h-7 px-2 rounded-md border text-[11.5px]" style={{ borderColor: HC.border }}><Users size={12} /></button>
              <button onClick={(e) => { e.stopPropagation(); exportMembers(r); }}
                className="h-7 px-2 rounded-md border text-[11.5px]" style={{ borderColor: HC.border }}><Download size={12} /></button>
              <button onClick={async (e) => { e.stopPropagation(); await api.post(`/crm/segments/${r.id}/duplicate`); load(); toast.success("Segment duplicated"); }}
                className="h-7 px-2 rounded-md border text-[11.5px]" style={{ borderColor: HC.border }}><Copy size={12} /></button>
            </span>
          ),
        }}
        onRowClick={(r) => { setEdit(JSON.parse(JSON.stringify(r))); setPreview(null); }}
        empty="No segments yet — create your first audience" />

      {edit && meta && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-2xl p-5 space-y-3 my-8" style={{ borderColor: HC.border }} data-testid="segment-builder">
            <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>{edit.id ? "Edit segment" : "New segment"}</h3>
            <input placeholder="Segment name *" value={edit.name} data-testid="segment-name"
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
            <input placeholder="Description" value={edit.description || ""} data-testid="segment-description"
              onChange={(e) => setEdit({ ...edit, description: e.target.value })}
              className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
            <div className="flex items-center gap-2 text-[12.5px]" style={{ color: HC.muted }}>
              Match
              <select value={edit.conditions.match} data-testid="segment-match"
                onChange={(e) => setEdit({ ...edit, conditions: { ...edit.conditions, match: e.target.value } })}
                className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                <option value="all">ALL conditions (AND)</option>
                <option value="any">ANY condition (OR)</option>
              </select>
              <div className="flex-1" />
              <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}
                data-testid="segment-status" className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {edit.conditions.rules.map((r, i) => {
              const opts = valueOptions(r.field);
              return (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`segment-rule-${i}`}>
                  <select value={r.field || ""} onChange={(e) => setRule(i, { field: e.target.value, value: "" })}
                    data-testid={`segment-rule-field-${i}`} className="h-9 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                    <option value="">Select field…</option>
                    {meta.fields.map((f) => <option key={f.field} value={f.field}>{f.label}</option>)}
                  </select>
                  <select value={r.op || "eq"} onChange={(e) => setRule(i, { op: e.target.value })}
                    data-testid={`segment-rule-op-${i}`} className="h-9 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                    {meta.operators.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {opts ? (
                    <select value={r.value || ""} onChange={(e) => setRule(i, { value: e.target.value })}
                      data-testid={`segment-rule-value-${i}`} className="h-9 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                      <option value="">Select…</option>
                      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input value={r.value || ""} onChange={(e) => setRule(i, { value: e.target.value })}
                      data-testid={`segment-rule-value-${i}`} placeholder="Value"
                      className="h-9 px-2 rounded-md border text-[12px] w-40" style={{ borderColor: HC.border }} />
                  )}
                  <button onClick={() => setEdit({ ...edit, conditions: { ...edit.conditions, rules: edit.conditions.rules.filter((_, x) => x !== i) } })}
                    className="text-[12px]" style={{ color: HC.red }}>Remove</button>
                </div>
              );
            })}
            <Btn variant="outline" data-testid="segment-add-rule"
              onClick={() => setEdit({ ...edit, conditions: { ...edit.conditions, rules: [...edit.conditions.rules, { field: "", op: "eq", value: "" }] } })}>
              <Plus size={14} /> Add condition
            </Btn>

            {preview && (
              <div className="rounded-xl p-3 text-[12.5px]" style={{ background: HC.greenTint, color: HC.green }} data-testid="segment-preview-result">
                {preview.count} customers match · sample: {preview.sample.slice(0, 5).map((s) => s.name).join(", ") || "none"}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Btn variant="outline" onClick={() => { setEdit(null); setPreview(null); }}>Cancel</Btn>
              <Btn variant="outline" onClick={runPreview} data-testid="segment-preview"><Eye size={14} /> Preview audience</Btn>
              <Btn onClick={save} data-testid="segment-save">Save segment</Btn>
            </div>
          </div>
        </div>
      )}

      {members && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-4xl p-5 space-y-3 my-8" style={{ borderColor: HC.border }} data-testid="segment-members-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>
                {members.segment.name} — {members.total} members
              </h3>
              <Btn variant="outline" onClick={() => setMembers(null)}>Close</Btn>
            </div>
            <DataTable testid="segment-members-table" rows={members.rows}
              columns={[["name", "Customer"], ["phone", "Phone"], ["location_name", "Location"],
                        ["loyalty_tier", "Tier"], ["orders", "Orders"], ["spend", "Spend"], ["last_purchase", "Last purchase"]]}
              render={{ spend: (r) => cmoney(r.spend), last_purchase: (r) => dt(r.last_purchase) }}
              onRowClick={(r) => navigate(`/crm/customers/${r.id}`)} empty="No customers match this segment" />
          </div>
        </div>
      )}
    </div>
  );
}
